/**
 * Viewport culling for the float band.
 *
 * The layout algorithm takes an optional world-frame arc and drops anything
 * outside it. Working out that arc is a rendering concern, so it lives here.
 *
 * Floats are placed at the *rotated* world point, so a world angle `a` is on
 * screen when `polar(a + rotation, r)` falls inside the viewport — the rotation
 * is added when sampling and the arc that comes back is rotation-free, which is
 * what `layoutFloats` wants.
 */
import { TAU, normalizeAngle, type ViewState } from '@circle/shared';

export interface Arc {
  angleFrom: number;
  angleTo: number;
}

/** Angular resolution of the sweep: half a degree is finer than any float. */
const SAMPLES = 720;
const STEP = TAU / SAMPLES;

/**
 * The sweep runs on every view change, including every frame of a spin, so the
 * sample directions are tabulated once and the rotation folded in with the
 * angle-sum identities — two trig calls per sweep instead of 1440.
 */
const SIN = new Float64Array(SAMPLES);
const COS = new Float64Array(SAMPLES);
for (let i = 0; i < SAMPLES; i++) {
  SIN[i] = Math.sin(i * STEP);
  COS[i] = Math.cos(i * STEP);
}

/**
 * The arc of the ring the viewport can see, or `null` when the whole band is
 * visible (the common case at fit zoom — then culling is skipped entirely).
 *
 * `radii` are the world radii to test; passing the inner and outer edge of the
 * float band is enough, given the margin.
 */
export function visibleArc(
  view: ViewState,
  width: number,
  height: number,
  radii: number[],
  marginPx = 140,
): Arc | null {
  const s = view.scale;
  if (!(s > 0) || radii.length === 0) return null;

  const m = marginPx / s;
  const x0 = -view.tx / s - m;
  const x1 = (width - view.tx) / s + m;
  const y0 = -view.ty / s - m;
  const y1 = (height - view.ty) / s + m;

  const rMax = Math.max(...radii);
  if (x0 <= -rMax && x1 >= rMax && y0 <= -rMax && y1 >= rMax) return null;

  const cosR = Math.cos(view.rotation);
  const sinR = Math.sin(view.rotation);
  const inside: boolean[] = new Array(SAMPLES);
  let seenIn = false;
  let seenOut = false;
  for (let i = 0; i < SAMPLES; i++) {
    // polarToXY(i * STEP + rotation, r), expanded.
    const s = SIN[i]! * cosR + COS[i]! * sinR;
    const c = COS[i]! * cosR - SIN[i]! * sinR;
    let ok = false;
    for (const r of radii) {
      const x = r * s;
      const y = -r * c;
      if (x >= x0 && x <= x1 && y >= y0 && y <= y1) {
        ok = true;
        break;
      }
    }
    inside[i] = ok;
    if (ok) seenIn = true;
    else seenOut = true;
  }

  if (!seenOut) return null;
  // Nothing on the band is on screen: an empty arc culls (almost) everything.
  if (!seenIn) return { angleFrom: 0, angleTo: 0 };

  // Longest circular run of hidden samples; the visible arc is its complement.
  let bestStart = 0;
  let bestLen = 0;
  let runStart = -1;
  let runLen = 0;
  for (let k = 0; k < 2 * SAMPLES; k++) {
    const i = k % SAMPLES;
    if (inside[i]) {
      runLen = 0;
      runStart = -1;
      continue;
    }
    if (runStart < 0) runStart = i;
    runLen++;
    if (runLen > bestLen && runLen <= SAMPLES) {
      bestLen = runLen;
      bestStart = runStart;
    }
  }

  const hiddenEnd = (bestStart + bestLen - 1) % SAMPLES;
  // One sample of slop on each side, so a float straddling the boundary lives.
  return {
    angleFrom: normalizeAngle(hiddenEnd * STEP),
    angleTo: normalizeAngle(bestStart * STEP),
  };
}

/** How far two arcs' endpoints have moved; used to decide when to re-pack. */
export function arcDrift(a: Arc | null, b: Arc | null): number {
  if (a === null && b === null) return 0;
  if (a === null || b === null) return Infinity;
  return Math.max(
    Math.abs(shortest(a.angleFrom, b.angleFrom)),
    Math.abs(shortest(a.angleTo, b.angleTo)),
  );
}

function shortest(a: number, b: number): number {
  const d = normalizeAngle(b - a);
  return d > Math.PI ? d - TAU : d;
}
